import { DISPLAY_TZ_OFFSET_MS } from '../lib/dateUtils';

const DAY_MS = 86_400_000;

// Task 12 (2026-07-30): the ONE window scheme every windowed reporting
// surface uses — General Insights AND Sentiment Intelligence. Sentiment
// previously had its own, different '1w'|'2w'|'4w'|'full' scheme
// (SentimentWindowId, now deleted); it's folded into this type so the two
// tabs can never drift apart again.
export type TimePeriod = '1w' | '1m' | '6m' | 'full';

// Shared option list both windowed reporting dropdowns render, in order —
// one canonical array so "Sentiment matches Overview exactly" is structural,
// not something that has to be kept in sync by hand across two files.
export const TIME_PERIOD_OPTIONS: Array<{ id: TimePeriod; label: string }> = [
  { id: '1w', label: '1 Week' },
  { id: '1m', label: '1 Month' },
  { id: '6m', label: '6 Month' },
  { id: 'full', label: 'Full Campaign' },
];

/** Window shown when the URL carries no `?win`: the whole campaign, whatever
 *  its age. */
export const DEFAULT_TIME_PERIOD: TimePeriod = 'full';

/**
 * The rolling-window START calc for General Insights' local fallback window
 * (the backend owns the real window; this draws only while the report is
 * missing):
 *
 *   1w   → trailing 7 days from `endMs` (fixed)
 *   1m   → `endMs` minus 1 calendar month (`setUTCMonth`, so month-length
 *          differences — 28/30/31 days — are handled correctly, not
 *          approximated as a fixed 30-day span)
 *   6m   → `endMs` minus 6 calendar months, same mechanism
 *   full → `firstLiveAt` when known, else `fallbackStartMs`, which the
 *          caller supplies (a stand-in trailing week).
 */
export function rollingWindowStart(
  timePeriod: TimePeriod,
  endMs: number,
  firstLiveAt: string | undefined,
  fallbackStartMs: number,
): number {
  if (timePeriod === '1w') return endMs - 7 * DAY_MS;
  if (timePeriod === '1m') {
    const d = new Date(endMs);
    d.setUTCMonth(d.getUTCMonth() - 1);
    return d.getTime();
  }
  if (timePeriod === '6m') {
    const d = new Date(endMs);
    d.setUTCMonth(d.getUTCMonth() - 6);
    return d.getTime();
  }
  const liveTs = firstLiveAt ? Date.parse(firstLiveAt) : NaN;
  return Number.isFinite(liveTs) ? liveTs : fallbackStartMs;
}

export interface SentimentRange {
  from?: string;
  to: string;
}

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

function monthsBack(today: Date, months: number): Date {
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth() - months;
  const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m, Math.min(today.getUTCDate(), lastDay)));
}

/** Mirrors the backend's WindowStart so a preset resolves to the same
 *  inclusive UTC `from`/`to` the server would; `full` omits `from`. */
export function sentimentRangeFor(preset: TimePeriod, todayUtc: string): SentimentRange {
  const today = new Date(`${todayUtc}T00:00:00Z`);
  switch (preset) {
    case '1w':
      return { from: isoDay(new Date(today.getTime() - 6 * DAY_MS)), to: todayUtc };
    case '1m':
      return { from: isoDay(monthsBack(today, 1)), to: todayUtc };
    case '6m':
      return { from: isoDay(monthsBack(today, 6)), to: todayUtc };
    default:
      return { to: todayUtc };
  }
}

export interface AxisTickLabel {
  /** Always shown — a day number ("15") for daily-granularity ticks, or the
   *  bare month/year for monthly/yearly ticks. */
  primary: string;
  /** Month name, shown only on the leftmost tick or a month-boundary tick
   *  within daily granularity — null everywhere else (including every tick
   *  in monthly/yearly granularity, which already carries the month/year as
   *  `primary` and has no day component to disambiguate). */
  secondary: string | null;
}

// Axis granularity by the span the chart actually shows: day numbers up to a
// month, month names up to a year, years beyond. The 1m window starts at the
// start of the day a calendar month back and runs to now, so after a 31-day
// month it spans up to 32 days; the daily cutoff has to cover that.
const DAILY_TICK_SPAN_MS = 8 * DAY_MS;
const DAILY_LABEL_SPAN_MS = 32 * DAY_MS;
const MONTHLY_SPAN_MS = 365 * DAY_MS;
const WEEK_MS = 7 * DAY_MS;

function stepTicks(start: number, end: number, stepMs: number): number[] {
  const ticks: number[] = [];
  for (let t = start; t <= end; t += stepMs) ticks.push(t);
  return ticks;
}

/** The IST wall-clock time as a Date read with UTC getters, matching the
 *  backend's IST day buckets whatever the browser's zone. */
const istWallClock = (ts: number): Date => new Date(ts + DISPLAY_TZ_OFFSET_MS);

const monthName = (wall: Date): string => wall.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });

/** `start`, then each IST boundary after it up to `end`; `advance` moves the
 *  cursor to the next month or year start. */
function boundaryTicks(start: number, end: number, toFirstBoundary: (d: Date) => void, advance: (d: Date) => void): number[] {
  const ticks = [start];
  const cursor = istWallClock(start);
  toFirstBoundary(cursor);
  cursor.setUTCHours(0, 0, 0, 0);
  const instant = (): number => cursor.getTime() - DISPLAY_TZ_OFFSET_MS;
  if (instant() <= start) advance(cursor);
  while (instant() <= end) {
    ticks.push(instant());
    advance(cursor);
  }
  return ticks;
}

/** Ticks for the views chart, always starting at `start` so its leftmost tick
 *  sits on the first point: daily up to 8 days, weekly up to a month, then
 *  month starts, then year starts. */
export function axisTicks(start: number, end: number): number[] {
  const span = end - start;
  if (span <= DAILY_TICK_SPAN_MS) return stepTicks(start, end, DAY_MS);
  if (span <= DAILY_LABEL_SPAN_MS) return stepTicks(start, end, WEEK_MS);
  if (span <= MONTHLY_SPAN_MS) {
    return boundaryTicks(start, end, (d) => d.setUTCDate(1), (d) => d.setUTCMonth(d.getUTCMonth() + 1));
  }
  return boundaryTicks(start, end, (d) => d.setUTCMonth(0, 1), (d) => d.setUTCFullYear(d.getUTCFullYear() + 1));
}

/** Two-line axis tick label ({primary, secondary}) — driven by the span the
 *  chart shows, the same rule `axisTicks` uses: day numbers up to a month,
 *  bare month names up to a year, years beyond.
 *
 *  Daily-granularity ticks repeat the day number every tick and only surface
 *  the month name at the leftmost tick or a month boundary ("1 / Jul") — the
 *  old design repeated "Jun 15", "Jun 16", "Jun 17"... on every single tick,
 *  which is what caused the reported congestion on long Full Campaign ranges. */
export function formatAxisTick(
  ts: number,
  { windowStart, spanMs }: { windowStart: number; spanMs: number },
): AxisTickLabel {
  const d = istWallClock(ts);
  if (Number.isNaN(d.getTime())) return { primary: '', secondary: null };

  if (spanMs > MONTHLY_SPAN_MS) return { primary: String(d.getUTCFullYear()), secondary: null };
  if (spanMs > DAILY_LABEL_SPAN_MS) return { primary: monthName(d), secondary: null };

  const isLeftmost = ts === windowStart;
  const isMonthStart = d.getUTCDate() === 1;
  return {
    primary: String(d.getUTCDate()),
    secondary: isLeftmost || isMonthStart ? monthName(d) : null,
  };
}
